// components/Navbar/SearchResults.js
"use client";
import React from "react";
import { useRouter } from "next/navigation";
import { useLanguage } from '../../app/contexts/LanguageContext'; // To'g'ri path

const SearchResults = ({ isMobile = false, searchProps }) => {
  const router = useRouter();
  const { t, isHydrated, currentLang } = useLanguage(); // currentLang ham qo'shildi
  const { 
    showResults, 
    searchQuery, 
    isLoading, 
    searchResults, 
    handleProductClick,
    handleImageError,
    setShowResults,
    setIsSearchOpen,
    setSearchQuery 
  } = searchProps;

  // Hydration tugashini kutish
  if (!isHydrated) {
    return (
      <div className="w-full p-8 text-center">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
          <div className="space-y-3">
            {[...Array(3)].map((_, index) => (
              <div key={index} className="flex items-center gap-4 p-4">
                <div className="w-16 h-16 bg-gray-200 rounded-xl"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!showResults || searchQuery.trim().length < 2) return null;

  return (
    <div className="w-full" key={`search-results-${currentLang}`}>
      {isLoading ? (
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-blue-500 mx-auto"></div>
          <p className="text-gray-600 mt-4 text-sm font-medium">{t.searching}</p>
        </div>
      ) : searchResults.length > 0 ? (
        <div className="divide-y divide-gray-100">
          {searchResults.slice(0, 5).map((forklift) => (
            <SearchResultItem
              key={`${forklift.id}-${currentLang}`}
              forklift={forklift}
              onProductClick={(id, event) => {
                // Search overlay va dropdownlarni yopish
                if (setIsSearchOpen) setIsSearchOpen(false);
                if (setShowResults) setShowResults(false);
                if (setSearchQuery) setSearchQuery("");
                
                // Product sahifasiga o'tish - to'g'ri URL
                event.preventDefault();
                event.stopPropagation();
                
                // Product ma'lumotlarini localStorage ga saqlash
                const product = searchResults.find((p) => p.id === id);
                if (product) {
                  localStorage.setItem("selectedProduct", JSON.stringify(product));
                }
                
                // To'g'ri router path - CategoryDetailPage ko'rsatadigan yo'l bo'yicha
                router.push(`/karaDetail/${id}`);
              }}
              onImageError={handleImageError}
              t={t}
              currentLang={currentLang}
            />
          ))}
          {searchResults.length > 5 && (
            <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 text-center border-t border-gray-100 mx-4 mb-4 rounded-xl">
              <p className="text-sm text-gray-700 mb-3">
                {t.moreResults || "Yana"} <span className="font-semibold text-blue-600">{searchResults.length - 5}</span> {t.resultsCount || "ta natija"}
              </p>
              <button
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  
                  // Search overlay va dropdownlarni yopish
                  if (setShowResults) setShowResults(false);
                  if (setIsSearchOpen) setIsSearchOpen(false);
                  if (setSearchQuery) setSearchQuery("");
                  
                  // Products sahifasiga o'tish
                  router.push(`/products?search=${encodeURIComponent(searchQuery)}`);
                }}
                className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium hover:bg-blue-100 px-4 py-2 rounded-lg transition-all duration-200 cursor-pointer"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                {t.seeAllResults || "Barchasini ko'rish"}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="p-8 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <p className="text-gray-600 text-sm font-medium">{t.noResults || "Natija topilmadi"}</p>
          <p className="text-gray-400 text-xs mt-1">{t.tryOtherKeywords || "Boshqa kalit so'zlarni sinab ko'ring"}</p>
        </div>
      )}
    </div>
  );
};

const SearchResultItem = ({ forklift, onProductClick, onImageError, t, currentLang }) => {
  return (
    <div
      className="p-4 hover:bg-gray-50 transition-all duration-200 cursor-pointer focus:bg-gray-50 focus:outline-none group mx-4 rounded-lg"
      onClick={(event) => onProductClick(forklift.id, event)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          onProductClick(forklift.id, e);
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={`${forklift.name} mahsulotiga o'tish`}
    >
      <div className="flex items-center gap-4">
        <div className="relative w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center overflow-hidden shadow-sm group-hover:shadow-md transition-all duration-200 flex-shrink-0">
          {forklift.images && forklift.images.length > 0 ? (
            <>
              <img
                src={forklift.images[0].image}
                alt={forklift.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                onError={onImageError}
                loading="lazy"
              />
              <div className="image-placeholder absolute inset-0 hidden items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100">
                <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2H9a2 2 0 00-2 2v10z" />
                </svg>
              </div>
            </>
          ) : (
            <div className="text-center">
              <svg className="w-8 h-8 text-blue-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2H9a2 2 0 00-2 2v10z" />
              </svg>
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-white text-sm line-clamp-2 group-hover:text-blue-600 transition-colors duration-200 leading-5">
            {forklift.name}
          </h4>
          <div className="flex items-center gap-2 mt-2">
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {forklift.capacity_kg ? `${(forklift.capacity_kg / 1000).toFixed(1)} ${t.ton || "ton"}` : "N/A"}
            </span>
          </div>
          <p className="text-xs text-white font-medium mt-2 flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-full bg-green-400"></span>
            {t.available || "Mavjud"}
          </p>
        </div>

        <div className="text-right flex-shrink-0">
          <div className="flex items-center justify-end">
            <svg className="w-5 h-5 text-gray-400 group-hover:text-blue-500 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchResults;